import type { JSX } from "react";

import { addMessage } from "@asurion-core/expert-workspace-timeline";
import { type Intent, assistMessageSuggestions, useVerifiedCustomerStore } from "@expert/sales-tools";
import { Button, Flex, Space, Stack } from "@mantine/core";
import { isEmail, isNotEmpty, useForm } from "@mantine/form";
import React, { useState } from "react";

import type { StepProps } from "@/customer-panel/components/call-intro-flow/steps/models";

import { getRootDispatcher, withPolymorphicClickAnalytics } from "@/analytics";
import { HandshakeAnimation } from "@/common-ui/animations/handshake";
import { useIVRInfo } from "@/customer-info/api";
import { Header } from "@/customer-panel/components/call-intro-flow/steps/Header";
import { NavButtons } from "@/customer-panel/components/call-intro-flow/steps/NavButtons";
import { SalesQuestionnaire } from "@/customer-panel/components/call-intro-flow/steps/sales-questionnaire/SalesQuestionnaire";
import { isEnoughInfoGatheredToDetermineEligibility } from "@/customer-panel/components/call-intro-flow/utils/isEnoughInfoGatheredToDetermineEligibility";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { isVoiceTask, useMostRecentTask, useSessionId } from "@/sdk";
import { isInternationalCaller } from "@/shared-utils";

import { CallReasonForm } from "./CallReasonForm";
import { CustomerInfoForm } from "./CustomerInfoForm";
import { IVRSummary } from "./IVRSummary";

export function CustomerInfoCollectionStep({ onNext, onPrev }: StepProps): JSX.Element {
    const sessionId = useSessionId();
    const customer = useVerifiedCustomerStore((state) => state.getVerifiedCustomer(sessionId).customer);
    const setCustomer = useVerifiedCustomerStore((state) => state.setCustomer);
    const ivrInfo = useIVRInfo();
    const task = useMostRecentTask();
    const voiceTask = isVoiceTask(task) ? task : null;
    const hasInfoFromIVR = !!ivrInfo.intent || !!ivrInfo.summary;
    const {
        features: { isSalesQuestionnaireEnabled = false },
        salesQuestionnaireProps: { plans } = {},
    } = usePartnerConfig();

    const [intent, setIntent] = useState<Intent | undefined>(customer?.intent ?? (ivrInfo.intent as Intent));
    const ButtonWithAnalytics = withPolymorphicClickAnalytics(Button<"button">, "Click");

    const form = useForm({
        validateInputOnBlur: true,
        initialValues: {
            email: customer?.email ?? ivrInfo.email ?? "",
            firstName: customer?.firstName ?? "",
            lastName: customer?.lastName ?? "",
            isInternationalCaller: voiceTask?.mdn ? isInternationalCaller(voiceTask?.mdn) : false,
            currentPlan: customer?.productName ?? null,
        },
        validate: {
            // fixme: remove type assertions once a fix lands for [form.isValid(): Type 'Promise<boolean>' is not assignable to type 'boolean'. · Issue #8880 · mantinedev/mantine](https://github.com/mantinedev/mantine/issues/8880)
            email: isEmail("Invalid email") as (value: unknown) => Awaited<React.ReactNode>,
            firstName: isNotEmpty("Enter the customer’s first name") as (value: unknown) => Awaited<React.ReactNode>,
            lastName: isNotEmpty("Enter the customer’s last name") as (value: unknown) => Awaited<React.ReactNode>,
        },
    });

    const handleNext =
        intent && onNext
            ? () => {
                  setCustomer(sessionId, {
                      ...customer,
                      ...form.getValues(),
                      fullName: `${form.getValues().firstName} ${form.getValues().lastName}`,
                      intent,
                      mdn: voiceTask?.mdn,
                      productName: form.getValues().currentPlan ?? undefined,
                      metadata: {
                          isInternationalCaller: form.getValues().isInternationalCaller,
                      },
                  });
                  addMessage(sessionId, {
                      id: crypto.randomUUID(),
                      text: assistMessageSuggestions[intent],
                      type: "Default",
                      isUnread: true,
                      status: "active",
                      key: "intentDetected",
                  });
                  onNext();
                  void getRootDispatcher().dispatchBusinessEvent("SalesQuestionnaireInfoGathered", {
                      isInternationalCaller: form.getValues().isInternationalCaller,
                      currentPlan: form.getValues().currentPlan,
                  });
                  void getRootDispatcher().dispatchBusinessEvent("CustomerIntentCollected", {
                      intent,
                  });
              }
            : undefined;

    return (
        <Stack mt="sm" w="100%">
            {hasInfoFromIVR && (
                <Stack align="center">
                    <HandshakeAnimation width="40%" />
                    <Header text="IVR handoff" />
                </Stack>
            )}
            {ivrInfo.intent && intent ? (
                <Stack gap={0}>
                    <IVRSummary intent={intent} summary={ivrInfo.summary} />
                    <Flex w="100%" justify="flex-end">
                        <ButtonWithAnalytics
                            variant="subtle"
                            c="dark.0"
                            td="underline"
                            p="0"
                            onClick={() => setIntent(undefined)}
                            analytics={{ actionId: "IncorrectCallReason" }}
                        >
                            Change reason
                        </ButtonWithAnalytics>
                    </Flex>
                </Stack>
            ) : (
                <CallReasonForm intent={intent} setIntent={setIntent} />
            )}
            <Stack gap={0} w="100%">
                <CustomerInfoForm form={form} />
                <Space h="md" />
                {plans && (
                    <>
                        <SalesQuestionnaire form={form} plans={plans} />
                        <Space h="md" />
                    </>
                )}
                <NavButtons
                    onNext={
                        form.isValid() &&
                        isEnoughInfoGatheredToDetermineEligibility(isSalesQuestionnaireEnabled, form.values) &&
                        intent &&
                        onNext
                            ? handleNext
                            : undefined
                    }
                    onPrev={onPrev}
                />
            </Stack>
        </Stack>
    );
}
